import React from 'react';
import Image from '@plone/volto/components/theme/Image/Image';
import { MPFLink } from 'volto-ministerio-publico-federal/components/UI';
import { EditItemButton } from './EditItemButton';
import { PlayBadge } from './DestaqueCard';
import {
  getOndeAtuaImageProps,
  youtubeThumb,
  type OndeAtuaCard as OndeAtuaCardModel,
  type YoutubeVideoRef,
} from './helpers';

interface OndeAtuaCardProps {
  card: OndeAtuaCardModel;
  isEditMode?: boolean;
  /** Abre o player. Ausente quando o card não é vídeo. */
  onPlay?: (video: YoutubeVideoRef) => void;
  /** Presente só no editor: abre este card na barra lateral. */
  onEditItem?: (index: number) => void;
}

const IMAGE_CLASS =
  'absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03] motion-reduce:transform-none motion-reduce:transition-none';

/**
 * Card do carrossel de "Onde o MPF atua?": foto arredondada em cima, legenda
 * abaixo (fora da imagem), no mesmo espírito do `DestaqueGridCard`.
 *
 * Duas formas de clique, nunca as duas juntas:
 *   - sem vídeo, o card inteiro é o link (`after:absolute after:inset-0`);
 *   - com vídeo, a foto é um botão que abre o player, e a legenda continua
 *     link só se houver conteúdo escolhido. Um overlay de link por cima do
 *     botão faria os dois brigarem pelo mesmo clique.
 */
export const OndeAtuaCard: React.FC<OndeAtuaCardProps> = ({
  card,
  isEditMode,
  onPlay,
  onEditItem,
}) => {
  const [thumbFallback, setThumbFallback] = React.useState(false);

  let imageProps = getOndeAtuaImageProps(card);
  // `maxresdefault` não existe para vídeo publicado fora de HD; `hqdefault`
  // existe sempre. Pede-se a boa e troca-se pela garantida se ela falhar.
  const isYoutubeThumb =
    !!card.videoId &&
    !!imageProps &&
    'src' in imageProps &&
    imageProps.src === youtubeThumb(card.videoId);
  if (isYoutubeThumb && thumbFallback) {
    imageProps = { src: youtubeThumb(card.videoId as string, 'high') };
  }

  const isVideo = !!card.videoId;
  const playLabel = card.title
    ? `Assistir ao vídeo: ${card.title}`
    : 'Assistir ao vídeo';

  const media = (
    <>
      {imageProps && (
        <Image
          {...imageProps}
          alt={card.alt}
          loading="lazy"
          className={IMAGE_CLASS}
          onError={
            isYoutubeThumb && !thumbFallback
              ? () => setThumbFallback(true)
              : undefined
          }
        />
      )}
      {isVideo && <PlayBadge />}
    </>
  );

  return (
    <article className="group relative min-w-0">
      <EditItemButtonSlot card={card} onEditItem={onEditItem} />

      {/* `relative` próprio: foto e selo de play se posicionam pela moldura da
          imagem, não pelo card inteiro (que inclui a legenda). */}
      <div className="relative aspect-[3/2] overflow-hidden rounded-2xl bg-gray-800 dark:bg-night-700">
        {isVideo ? (
          <button
            type="button"
            aria-label={playLabel}
            onClick={() =>
              onPlay?.({ id: card.videoId as string, title: card.title })
            }
            className="absolute inset-0 h-full w-full cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            {media}
          </button>
        ) : (
          media
        )}
      </div>

      {/* Semibold como os demais títulos de card da capa: `font-normal` dava
          a este h3 o peso de corpo de texto.

          16px no mobile, como o Figma pede: em 18px o título do card empatava
          com o cabeçalho da própria faixa, que também é 18 no celular. */}
      <h3 className="mt-3 line-clamp-2 text-base font-semibold text-on-surface md:text-lg dark:text-night-100">
        <OndeAtuaCardLabel card={card} isEditMode={isEditMode} />
      </h3>
    </article>
  );
};

/** Lápis do card, só dentro do editor. */
const EditItemButtonSlot: React.FC<{
  card: OndeAtuaCardModel;
  onEditItem?: (index: number) => void;
}> = ({ card, onEditItem }) =>
  onEditItem ? (
    <EditItemButton
      label={`Editar card: ${card.title || 'sem título'}`}
      onClick={() => onEditItem(card.sourceIndex)}
      className="absolute right-2 top-2"
    />
  ) : null;

/**
 * Legenda do card.
 *
 * Vira link quando há conteúdo escolhido e o card não é vídeo — aí o `after`
 * estende a área clicável para o card inteiro sem inchar o nome acessível do
 * link. Sendo vídeo, o link fica restrito ao texto: quem responde pela foto é
 * o botão do player.
 */
const OndeAtuaCardLabel: React.FC<{
  card: OndeAtuaCardModel;
  isEditMode?: boolean;
}> = ({ card, isEditMode }) => {
  // Um `<a>` sem `href` não é clicável nem focável e ainda assim é anunciado
  // como link. No editor, navegar seria pior ainda: tira o editor da página.
  if (!card.href || isEditMode) return <>{card.title}</>;

  const overlay = card.videoId ? '' : ' after:absolute after:inset-0';

  return (
    <MPFLink
      href={card.href}
      openInNewTab={card.external || undefined}
      className={`cursor-pointer rounded text-on-surface hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 dark:text-night-100 dark:focus-visible:ring-white${overlay}`}
    >
      {card.title}
    </MPFLink>
  );
};

export default OndeAtuaCard;
